<script setup>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { resolveAceleresApiBase } from 'shared/helpers/aceleresApiBase';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const store = useStore();
const route  = useRoute();

const currentUser = computed(() => store.getters['auth/getCurrentUser']);
const accountId   = computed(() => route.params.accountId || '');

const LARAVEL_URL = `${resolveAceleresApiBase()}/api/aurora/vagas-config`;

const turmas    = ref([]);
const esgotadas = ref([]);
const loading   = ref(true);
const saving    = ref(false);
const savedMsg  = ref('');
const errorMsg  = ref('');

function headers() {
  return {
    'X-Chatwoot-Token':      currentUser.value?.access_token || '',
    'X-Chatwoot-Account-Id': accountId.value,
  };
}

async function carregar() {
  loading.value = true;
  errorMsg.value = '';
  try {
    const resp = await axios.get(LARAVEL_URL, { headers: headers() });
    turmas.value    = resp.data.turmas    || [];
    esgotadas.value = resp.data.esgotadas || [];
  } catch {
    errorMsg.value = 'Erro ao carregar configuração.';
  } finally {
    loading.value = false;
  }
}

function isEsgotada(num) {
  return esgotadas.value.includes(num);
}

function toggleTurma(num) {
  if (isEsgotada(num)) {
    esgotadas.value = esgotadas.value.filter(n => n !== num);
  } else {
    esgotadas.value = [...esgotadas.value, num];
  }
}

async function salvar() {
  saving.value   = true;
  savedMsg.value = '';
  errorMsg.value = '';
  try {
    await axios.patch(
      LARAVEL_URL,
      { esgotadas: esgotadas.value },
      { headers: headers() }
    );
    savedMsg.value = 'Salvo com sucesso!';
    setTimeout(() => { savedMsg.value = ''; }, 3000);
  } catch {
    errorMsg.value = 'Erro ao salvar. Tente novamente.';
  } finally {
    saving.value = false;
  }
}

onMounted(carregar);
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        title="Aurora IA — Vagas"
        description="Quando uma turma esgotar as vagas, ative o alerta abaixo. A Aurora irá transferir automaticamente o lead para um consultor humano em vez de enviar o link de checkout."
        :show-back-button="false"
      />
    </template>
    <template #body>
      <div class="mt-6 max-w-lg">

        <div v-if="loading" class="text-sm text-slate-500 py-4">Carregando...</div>

        <div v-else>
          <div
            v-for="turma in turmas"
            :key="turma.num"
            class="flex items-center justify-between rounded-lg border border-slate-200 p-4 mb-3"
          >
            <div class="flex-1 pr-4">
              <p class="text-sm font-medium text-slate-800">{{ turma.nome }}</p>
              <p
                class="text-xs mt-1"
                :class="isEsgotada(turma.num) ? 'text-red-600 font-semibold' : 'text-slate-400'"
              >
                {{
                  isEsgotada(turma.num)
                    ? '🔴 Vagas esgotadas — Aurora transfere para consultor'
                    : '🟢 Vagas disponíveis — Aurora envia link de checkout'
                }}
              </p>
            </div>
            <button
              type="button"
              class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none"
              :class="isEsgotada(turma.num) ? 'bg-red-500' : 'bg-slate-200'"
              @click="toggleTurma(turma.num)"
            >
              <span
                class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200"
                :class="isEsgotada(turma.num) ? 'translate-x-5' : 'translate-x-0'"
              />
            </button>
          </div>

          <div v-if="!turmas.length" class="text-sm text-slate-500 py-2">
            Nenhuma turma configurada.
          </div>

          <div class="mt-4 flex items-center gap-3">
            <button
              type="button"
              :disabled="saving"
              class="rounded-md bg-woot-500 px-4 py-2 text-sm font-medium text-white hover:bg-woot-600 disabled:opacity-50 cursor-pointer"
              @click="salvar"
            >
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </button>
            <span v-if="savedMsg" class="text-sm text-green-600 font-medium">{{ savedMsg }}</span>
            <span v-if="errorMsg" class="text-sm text-red-600">{{ errorMsg }}</span>
          </div>
        </div>

      </div>
    </template>
  </SettingsLayout>
</template>
